/**
 * A single message.
 *
 * @typedef {'text'|'image'|'video'|'audio'|'document'|'location'|'contact'|'system'} MessageType
 * @typedef {'pending'|'sent'|'delivered'|'read'|'failed'} MessageStatus
 *
 * @typedef {object} MediaMeta
 * @property {string}  [mimeType]
 * @property {number}  [sizeBytes]
 * @property {number}  [width]
 * @property {number}  [height]
 * @property {number}  [durationMs]
 * @property {string}  [fileName]
 * @property {string}  [thumbnailUrl]
 *
 * @typedef {object} Message
 * @property {string}        id
 * @property {string}        chatId
 * @property {string}        senderId
 * @property {MessageType}   type
 * @property {string}        body        Text, or the caption on media
 * @property {string|null}   mediaUrl
 * @property {MediaMeta|null} mediaMeta
 * @property {string|null}   replyToId
 * @property {MessageStatus} status
 * @property {Record<string, string[]>} reactions  emoji -> user ids
 * @property {Date}          createdAt
 * @property {Date|null}     editedAt
 * @property {Date|null}     deletedAt   Set means tombstoned for everyone
 */

import { toDate } from '../utils/time.js'

/**
 * @param {string} id
 * @param {object} raw
 * @param {string} [chatId]
 * @returns {Message}
 */
export function toMessage(id, raw = {}, chatId) {
  return {
    id,
    chatId:    chatId ?? raw.chatId ?? raw.chat_id ?? '',
    senderId:  raw.senderId ?? raw.sender_id ?? '',
    type:      raw.type ?? 'text',
    body:      raw.body ?? '',
    mediaUrl:  raw.mediaUrl ?? raw.media_url ?? null,
    mediaMeta: raw.mediaMeta ?? raw.media_meta ?? null,
    replyToId: raw.replyToId ?? raw.reply_to_id ?? null,
    status:    raw.status ?? 'sent',
    reactions: raw.reactions ?? {},
    createdAt: toDate(raw.createdAt ?? raw.created_at) ?? new Date(),
    editedAt:  toDate(raw.editedAt ?? raw.edited_at),
    deletedAt: toDate(raw.deletedAt ?? raw.deleted_at)
  }
}

/** One-line summary for the chat list row and for push notifications. */
export function previewText(message) {
  if (message.deletedAt) return 'This message was deleted'
  switch (message.type) {
    case 'image':    return message.body || 'Photo'
    case 'video':    return message.body || 'Video'
    case 'audio':    return 'Voice message'
    case 'document': return message.mediaMeta?.fileName || 'Document'
    case 'location': return 'Location'
    case 'contact':  return 'Contact'
    default:         return message.body
  }
}

export function isMine(message, currentUserId) {
  return message.senderId === currentUserId
}
